/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { NEVER, Observable } from 'rxjs';

import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpParams, HttpRequest } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { filter, first, switchMap } from 'rxjs/operators';
import { AppState, fromAppActions } from '../state';
import { FeatureFlagConfigService } from '../services/feature-flag-config.service';
import { FirebaseFeatureFlags } from '../enums/firebase-custom-claims.enum';
import { AuthService } from '../services/auth/auth.service';
import { FeatureFlagService } from '@shared/services/feature-flag.service';

export interface IFeatureFlagConfig {
  url: string;
  urlChanged: boolean;
  params?: HttpParams;
  contentType?: string;
  cancelled?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private featureFlagConfigService: FeatureFlagConfigService,
    private _store: Store<AppState>,
    private _featureFlagService: FeatureFlagService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.setStateFromAuth(req);

    return this.getFeatureFlaggedUrl(req).pipe(
      switchMap(featureFlaggedUrl => {
        const { urlChanged, url, contentType, params } = featureFlaggedUrl;

        if (!urlChanged) {
          return next.handle(req);
        }

        if (featureFlaggedUrl.cancelled) {
          return NEVER;
        }

        const clonedReq = req.clone({
          url,
          headers: this.getHeaders(req, contentType)
        });

        if (params) {
          const modifiedReqParams = clonedReq.clone({
            params
          });
          return next.handle(modifiedReqParams);
        }
        return next.handle(clonedReq);
      })
    );
  }

  private getHeaders(req: HttpRequest<any>, contentType?: string): HttpHeaders {
    if (!contentType) {
      return req.headers;
    }

    return new HttpHeaders({
      'content-type': contentType,
      authorization: req.headers.get('authorization')
    });
  }

  private setStateFromAuth(req: HttpRequest<any>) {
    const token = req.headers.get('authorization');

    let featureFlags: FirebaseFeatureFlags[] = [];
    let isMasquerading = false;

    if (token) {
      const decodedToken = this.authService.decodeToken(token);
      featureFlags = (decodedToken?.flags as FirebaseFeatureFlags[]) ?? [];

      isMasquerading = !!decodedToken?.ate?.masquerading;
    }

    // Straight from the token, not after the feature-flags fetch: features that
    // must stay off while masquerading (the Gmail inbox) read this before they load.
    this._store.dispatch(new fromAppActions.SetIsMasquerading(isMasquerading));

    this._featureFlagService
      .getFeatureFlags()
      .pipe(
        filter(({ flags }) => flags.length > 0),
        first()
      )
      .subscribe(({ flags }) => {
        const effectiveFlags = flags?.length ? flags : featureFlags;

        this._store.dispatch(new fromAppActions.SetFeatureFlags(effectiveFlags));
      });
  }

  private getFeatureFlaggedUrl(req: HttpRequest<any>): Observable<IFeatureFlagConfig> {
    return this.featureFlagConfigService.getUrl(req);
  }
}
